
import { useState, useEffect } from "react";
import { AIPersonality } from "@/components/settings/AIPersonalityPanel";
import { useAuth } from "@/components/AuthProvider";
import { sendMessageToMonolith } from "@/services/monolith-service";
import { toast } from "@/hooks/use-toast";
import { useAIPersonality } from "@/hooks/use-ai-personality";
import { supabase } from "@/integrations/supabase/client";

export const useAnalogHorrorMessage = (
  isOpen: boolean,
  message: string,
  customAiPersonality?: AIPersonality
) => {
  const [enhancedMessage, setEnhancedMessage] = useState<string | null>(null);
  const [isLoadingMessage, setIsLoadingMessage] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [glitching, setGlitching] = useState(false);
  const { user } = useAuth();

  // Use the provided AI personality directly if provided, otherwise use the hook 
  // with its value only being used if customAiPersonality isn't provided
  const { aiPersonality: defaultAiPersonality, userName } = useAIPersonality({ 
    includeUserName: true,
    enabled: isOpen && !customAiPersonality
  });
  
  // Prioritize any custom personality passed in as a prop
  const aiPersonality = customAiPersonality || defaultAiPersonality;

  // Generate enhanced message when popup opens
  useEffect(() => {
    if (isOpen && message && !enhancedMessage && !isLoadingMessage) {
      const generateEnhancedMessage = async () => {
        setIsLoadingMessage(true);
        try {
          console.log(`AnalogHorror: Generating enhanced message with personality ${aiPersonality}`);
          // Use the original message as base for enhancement
          const prompt = `Rewrite this message in your unique voice, keeping it brief (max 240 characters) and maintaining the core information: "${message}"`;
          const response = await sendMessageToMonolith(prompt, aiPersonality, userName);
          
          if (response?.response) {
            setEnhancedMessage(response.response);
          } else {
            setEnhancedMessage(message); // Fallback to original message
          }
        } catch (error) {
          console.error("Error generating enhanced message:", error);
          setEnhancedMessage(message); // Fallback to original message
        } finally {
          setIsLoadingMessage(false);
        }
      };
      
      generateEnhancedMessage();
    }
  }, [isOpen, message, enhancedMessage, aiPersonality, userName, isLoadingMessage]);

  // Reset enhanced message when popup closes
  useEffect(() => {
    if (!isOpen) {
      setEnhancedMessage(null);
      setIsSaving(false);
    }
  }, [isOpen]);

  // Glitch effect
  useEffect(() => {
    if (!isOpen) return;
    
    // Random glitch intervals
    const glitchInterval = setInterval(() => {
      if (Math.random() > 0.7) {
        setGlitching(true);
        setTimeout(() => setGlitching(false), 150);
      }
    }, 2000);
    
    return () => clearInterval(glitchInterval);
  }, [isOpen]);

  // Handle saving motivation to user's motivations
  const handleSaveMotivation = async () => {
    if (!user || !displayMessage || displayMessage === "CONNECTING TO MONOLITH..." || isSaving) return;
    
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('motivations')
        .insert([{ content: displayMessage, user_id: user.id }]);
      
      if (error) throw error;
      
      toast.success({
        title: "MOTIVATION SAVED",
        description: "This message has been added to your motivations"
      });
    } catch (error: any) {
      console.error("Error saving motivation:", error);
      toast.error({
        title: "ERROR",
        description: "Failed to save motivation"
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Display message or loading state
  const displayMessage = isLoadingMessage 
    ? "CONNECTING TO MONOLITH..." 
    : enhancedMessage || message;

  return {
    aiPersonality,
    glitching,
    isLoadingMessage,
    isSaving,
    displayMessage,
    handleSaveMotivation
  };
};
